import axios from "axios";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { EMPTY_GUID } from "../../../../../enum/Enum";

const authHeaders = (accessToken) => ({
    access_token: accessToken,
    Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
});

const apiUrl = (route) => `${window._env_.REACT_APP_APIS}/${route}`;

const isUnsavedHeader = (headerRowId) => !headerRowId || headerRowId === EMPTY_GUID;

const unsavedHeaderResult = () => ({ success: false, message: MESSAGES.SAVE_HEADER_BEFORE_PROCEEDING });

/* The CardConnect endpoints answer a refusal with 400 and the CardConnectPayResponseDTO envelope as the
   body, so the server's message is on error.response.data. */
const errorMessage = (error, fallback) => {
    const d = error.response?.data;
    return (d && (d.message || (typeof d === "string" ? d : ""))) || error.message || fallback;
};

/**
 * Server-side checks before CardConnectPay (executable path, header, etc.).
 * @param {string} headerRowId - OM transaction header ROWID (GUID string)
 * @param {string} accessToken
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function verifyCardConnectPay(headerRowId, accessToken) {
    if (isUnsavedHeader(headerRowId)) {
        return unsavedHeaderResult();
    }
    try {
        const { data } = await axios.get(apiUrl(routes.OM.VerifyCardConnectPay), {
            params: { headerRowId },
            headers: authHeaders(accessToken),
        });
        return { success: data.success === true, message: data.message };
    } catch (error) {
        return { success: false, message: errorMessage(error, "Verification failed.") };
    }
}

/**
 * Delphi uOMD0043 FormShow: the order the popup pays, read from the database rather than the page, so a
 * payment profile stored by an earlier "Create Profile and Pay" is offered.
 * @returns {Promise<{ success: boolean, message?: string, header?: { transactionNumber: string, customer: string, paymentProfile: string, amount: number } }>}
 */
export async function getCardConnectHeaderForPayment(headerRowId, accessToken) {
    if (isUnsavedHeader(headerRowId)) {
        return unsavedHeaderResult();
    }
    try {
        const { data } = await axios.get(apiUrl(routes.OM.GetCardConnectHeaderForPayment), {
            params: { headerRowId },
            headers: authHeaders(accessToken),
        });
        return {
            success: true,
            header: {
                transactionNumber: data?.transactionNumber ?? "",
                customer: data?.customerId ?? "",
                paymentProfile: data?.paymentProfile ?? "",
                amount: data?.amount != null ? Number(data.amount) : 0,
            },
        };
    } catch (error) {
        return { success: false, message: errorMessage(error, "Failed to load the order for payment.") };
    }
}

/**
 * Runs verify then loads the popup's order (used before opening the Card Connect Pay popup), as Delphi
 * opCardConnectPayClick checked OM Setup before showing uOMD0043.
 * @returns {Promise<{ success: boolean, message?: string, header?: object }>}
 */
export async function prepareCardConnectPayOpen(headerRowId, accessToken) {
    const verify = await verifyCardConnectPay(headerRowId, accessToken);
    if (!verify.success) {
        return { success: false, message: verify.message };
    }
    return getCardConnectHeaderForPayment(headerRowId, accessToken);
}

/**
 * Delphi opCardConnectPayClick after uOMD0043 returns mrOK: launch Card Connect with -method pay.
 * @param {string} profileToUse - "profile/account" when paying by profile, else ""
 * @param {string} newProfile - "New" when creating a profile, else ""
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function runCardConnectPay(headerRowId, profileToUse, newProfile, accessToken) {
    if (isUnsavedHeader(headerRowId)) {
        return unsavedHeaderResult();
    }
    try {
        const { data } = await axios.post(
            apiUrl(routes.OM.CardConnectPay),
            { headerRowId, profileToUse: profileToUse || "", newProfile: newProfile || "" },
            { headers: { "Content-Type": "application/json", ...authHeaders(accessToken) } }
        );
        return { success: data.success === true, message: data.message };
    } catch (error) {
        return { success: false, message: errorMessage(error, "Payment process failed.") };
    }
}

/**
 * Delphi opCardConnectVoidClick: header exists + OM Setup executable path (no pay type/stage rules).
 */
export async function verifyCardConnectVoidRefund(headerRowId, accessToken) {
    if (isUnsavedHeader(headerRowId)) {
        return unsavedHeaderResult();
    }
    try {
        const { data } = await axios.get(apiUrl(routes.OM.VerifyCardConnectVoidRefund), {
            params: { headerRowId },
            headers: authHeaders(accessToken),
        });
        return { success: data.success === true, message: data.message };
    } catch (error) {
        return { success: false, message: errorMessage(error, "Verification failed.") };
    }
}

/**
 * Delphi opCardConnectVoidClick: launch Card Connect with -method voidrefund.
 * @returns {Promise<{ success: boolean, message?: string }>}
 */
export async function runCardConnectVoidRefund(headerRowId, accessToken) {
    if (isUnsavedHeader(headerRowId)) {
        return unsavedHeaderResult();
    }
    try {
        const { data } = await axios.post(
            apiUrl(routes.OM.CardConnectVoidRefund),
            { headerRowId },
            { headers: { "Content-Type": "application/json", ...authHeaders(accessToken) } }
        );
        return { success: data.success === true, message: data.message };
    } catch (error) {
        return { success: false, message: errorMessage(error, "Refund process failed.") };
    }
}
